import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ── Light Palette ─────────────────────────────────────────────────────────────
const C = {
  bg:        [255, 255, 255],   // white
  pageBg:    [248, 249, 252],   // very light gray (alternate page bg)
  brand:     [99,  91, 255],    // brand purple
  header:    [99,  91, 255],    // alias for brand (table headers)
  brandDark: [70,  62, 200],    // darker purple
  brandLight:[237, 235, 255],   // tint for badges
  headerTxt: [255, 255, 255],
  rowAlt:    [248, 249, 252],
  rowNorm:   [255, 255, 255],
  border:    [226, 232, 240],
  text:      [30,  36,  55],    // near-black
  muted:     [100, 110, 130],   // slate
  mutedLt:   [180, 188, 204],   // lighter muted
  green:     [16,  185, 129],   // emerald
  greenBg:   [236, 253, 245],
  red:       [239, 68,  68],
  redBg:     [254, 242, 242],
  amber:     [245, 158, 11],
  amberBg:   [255, 251, 235],
  sky:       [14,  165, 233],
  white:     [255, 255, 255],
  // Chart scenario colors
  chartReal: [99,  91, 255],  // brand purple — realistic
  chartOpt:  [16,  185, 129], // emerald — optimistic
  chartPess: [245, 158, 11],  // amber — pessimistic
  chartTarget:[239, 68,  68], // red — target line
};

// ── Helpers ──────────────────────────────────────────────────────────────────
function newDoc() {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  // white background by default — no rect needed
  return doc;
}

function sectionTitle(doc, y, label) {
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.brand);
  doc.text(label.toUpperCase(), 14, y);
  doc.setDrawColor(...C.brand);
  doc.setLineWidth(0.25);
  doc.line(14, y + 1.2, 196, y + 1.2);
  return y + 6;
}

function fmtAmt(value, currency = 'BRL') {
  const locale = currency === 'BRL' ? 'pt-BR' : currency === 'EUR' ? 'de-DE' : 'en-US';
  return parseFloat(value || 0).toLocaleString(locale, {
    style: 'currency', currency, maximumFractionDigits: 2,
  });
}

function abbrevAmt(value, currency = 'BRL') {
  const abs = Math.abs(value);
  const locale = currency === 'BRL' ? 'pt-BR' : currency === 'EUR' ? 'de-DE' : 'en-US';
  const sym = (0).toLocaleString(locale, { style: 'currency', currency }).replace(/\d|\.|,|\s/g, '').trim();
  if (abs >= 1_000_000) return `${sym}${(value / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000)     return `${sym}${(value / 1_000).toFixed(0)}k`;
  return `${sym}${value.toFixed(0)}`;
}

function addMonths(date, n) {
  const d = new Date(date);
  d.setDate(1);
  d.setMonth(d.getMonth() + n);
  return d;
}

function fmtMonthYear(date) {
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }).replace(' ', '/');
}

function pageBreakIfNeeded(doc, y, needed = 40) {
  if (y + needed > 275) {
    doc.addPage();
    return 18;
  }
  return y;
}

function drawPageFooter(doc, pageNum, totalPages) {
  const y = 289;
  doc.setDrawColor(...C.border);
  doc.setLineWidth(0.2);
  doc.line(14, y - 3, 196, y - 3);
  doc.setFontSize(7);
  doc.setTextColor(...C.mutedLt);
  doc.setFont('helvetica', 'normal');
  doc.text('Plannizzi AI · Smart finance planning, powered by AI', 14, y);
  doc.text(`Page ${pageNum} / ${totalPages}`, 196, y, { align: 'right' });
  doc.setFontSize(6.5);
  doc.text(
    'This document is for informational purposes only and does not constitute financial advice.',
    105, y + 4, { align: 'center' }
  );
}

// Shared table style
function tStyles(extra = {}) {
  return {
    theme: 'plain',
    styles:      { fontSize: 8.5, cellPadding: 3, textColor: C.text, fillColor: C.rowNorm, lineColor: C.border, lineWidth: 0.1 },
    headStyles:  { fillColor: C.brand, textColor: C.headerTxt, fontStyle: 'bold', fontSize: 8.5 },
    alternateRowStyles: { fillColor: C.rowAlt },
    margin:      { left: 14, right: 14 },
    ...extra,
  };
}

// ── Projection Chart ─────────────────────────────────────────────────────────
function drawProjectionChart(doc, yTop, scenarios, effectiveCost, currency) {
  if (!scenarios?.length) return yTop;

  // Collect all data: each scenario's monthly_projection
  // monthly_projection entries: { month, savings_balance }
  const seriesDef = [
    { key: 'realistic',   label: 'Realistic',   color: C.chartReal },
    { key: 'optimistic',  label: 'Optimistic',  color: C.chartOpt  },
    { key: 'pessimistic', label: 'Pessimistic', color: C.chartPess },
  ];

  const activeSeries = seriesDef
    .map((def) => {
      const sc = scenarios.find((s) => s.scenario_type === def.key);
      if (!sc?.monthly_projection?.length) return null;
      const pts = [...sc.monthly_projection].sort((a, b) => a.month - b.month);
      return { ...def, points: pts };
    })
    .filter(Boolean);

  if (!activeSeries.length) return yTop;

  const maxMonth = Math.max(...activeSeries.flatMap((s) => s.points.map((p) => p.month)));
  const maxVal   = Math.max(
    ...activeSeries.flatMap((s) => s.points.map((p) => parseFloat(p.savings_balance || 0))),
    parseFloat(effectiveCost || 0)
  );
  const minVal = 0;

  // Layout
  const boxX = 14, boxY = yTop, boxW = 182, boxH = 72;
  const axisL = 34, axisB = 11;
  const plotX = boxX + axisL;
  const plotY = boxY;
  const plotW = boxW - axisL;
  const plotH = boxH - axisB;

  // Chart background
  doc.setFillColor(250, 250, 253);
  doc.setDrawColor(...C.border);
  doc.setLineWidth(0.2);
  doc.roundedRect(boxX, boxY - 1, boxW, boxH + 2, 2, 2, 'FD');

  // Coordinate helpers
  const toX = (m) => plotX + ((m - 1) / Math.max(maxMonth - 1, 1)) * plotW;
  const toY = (v) => plotY + plotH - (Math.max(0, v - minVal) / (maxVal - minVal)) * plotH;

  // Y-axis grid lines + labels (5 ticks)
  const yTicks = 5;
  doc.setFontSize(6);
  doc.setFont('helvetica', 'normal');
  for (let i = 0; i <= yTicks; i++) {
    const val = minVal + (maxVal - minVal) * (i / yTicks);
    const gy  = toY(val);
    doc.setDrawColor(...C.border);
    doc.setLineWidth(0.15);
    doc.line(plotX, gy, plotX + plotW, gy);
    doc.setTextColor(...C.muted);
    doc.text(abbrevAmt(val, currency), plotX - 2, gy + 1.5, { align: 'right' });
  }

  // X-axis tick labels (every 6 months, or every 3 if short)
  const xStep = maxMonth <= 18 ? 3 : maxMonth <= 36 ? 6 : 12;
  for (let m = xStep; m <= maxMonth; m += xStep) {
    const gx = toX(m);
    doc.setDrawColor(...C.border);
    doc.setLineWidth(0.15);
    doc.line(gx, plotY, gx, plotY + plotH);
    doc.setTextColor(...C.muted);
    doc.text(`M${m}`, gx, plotY + plotH + 7, { align: 'center' });
  }
  // X-axis base line
  doc.setDrawColor(...C.mutedLt);
  doc.setLineWidth(0.3);
  doc.line(plotX, plotY + plotH, plotX + plotW, plotY + plotH);
  // Y-axis base line
  doc.line(plotX, plotY, plotX, plotY + plotH);

  // Target reference line (dashed red)
  if (effectiveCost && parseFloat(effectiveCost) > 0) {
    const ty = toY(parseFloat(effectiveCost));
    if (ty >= plotY && ty <= plotY + plotH) {
      doc.setDrawColor(...C.chartTarget);
      doc.setLineWidth(0.4);
      doc.setLineDashPattern([1.5, 1], 0);
      doc.line(plotX, ty, plotX + plotW, ty);
      doc.setLineDashPattern([], 0);
      doc.setFontSize(6);
      doc.setTextColor(...C.chartTarget);
      doc.text(`Target: ${abbrevAmt(parseFloat(effectiveCost), currency)}`, plotX + plotW - 1, ty - 1.5, { align: 'right' });
    }
  }

  // Draw scenario lines
  for (const series of activeSeries) {
    const pts = series.points;
    if (pts.length < 2) continue;

    doc.setDrawColor(...series.color);
    doc.setLineWidth(0.8);
    doc.setLineDashPattern([], 0);

    for (let i = 1; i < pts.length; i++) {
      const x1 = toX(pts[i - 1].month);
      const y1 = toY(parseFloat(pts[i - 1].savings_balance || 0));
      const x2 = toX(pts[i].month);
      const y2 = toY(parseFloat(pts[i].savings_balance || 0));
      // Clamp to plot area
      if (y1 >= plotY && y2 >= plotY && y1 <= plotY + plotH && y2 <= plotY + plotH) {
        doc.line(x1, y1, x2, y2);
      }
    }
    // End dot
    const last = pts[pts.length - 1];
    const lx = toX(last.month);
    const ly = toY(parseFloat(last.savings_balance || 0));
    if (ly >= plotY && ly <= plotY + plotH) {
      doc.setFillColor(...series.color);
      doc.circle(lx, ly, 1, 'F');
    }
  }

  // Legend (bottom right inside chart)
  const legendX = plotX + plotW - 2;
  let legendY = plotY + 6;
  for (const series of activeSeries) {
    doc.setFillColor(...series.color);
    doc.rect(legendX - 22, legendY - 2.5, 5, 1.8, 'F');
    doc.setFontSize(6.5);
    doc.setTextColor(...C.text);
    doc.text(series.label, legendX - 15, legendY - 1.2);
    legendY += 5;
  }
  // Target legend
  doc.setDrawColor(...C.chartTarget);
  doc.setLineDashPattern([1, 0.8], 0);
  doc.setLineWidth(0.5);
  doc.line(legendX - 22, legendY - 1.5, legendX - 17, legendY - 1.5);
  doc.setLineDashPattern([], 0);
  doc.setFontSize(6.5);
  doc.setTextColor(...C.chartTarget);
  doc.text('Target', legendX - 15, legendY - 1.2);

  return yTop + boxH + 8;
}

// ─────────────────────────────────────────────────────────────────────────────
// PLAN PDF
// ─────────────────────────────────────────────────────────────────────────────
const PRIORITY_LABEL = { 1: 'Critical', 2: 'High', 3: 'Medium', 4: 'Low', 5: 'Optional' };
const CATEGORY_LABEL = {
  investment:          'Invest',
  expense_reduction:   'Save',
  budget_reallocation: 'Budget',
  behavior:            'Habit',
};

export function exportPlanPdf(plan, progressData, currency = 'BRL') {
  const doc = newDoc();

  const goalType = plan.goal_type
    ? plan.goal_type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    : '';
  const deadline = plan.goal_deadline
    ? new Date(plan.goal_deadline).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : null;

  // ── Cover header ─────────────────────────────────────────────────────────
  // Top bar
  doc.setFillColor(...C.brand);
  doc.rect(0, 0, 210, 20, 'F');
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.white);
  doc.text('Plannizzi AI', 14, 12);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(210, 207, 255);
  doc.text('Smart finance planning, powered by AI', 14, 17);
  doc.setTextColor(...C.white);
  doc.text(
    `Generated: ${new Date().toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })}`,
    196, 14, { align: 'right' }
  );

  let y = 28;

  // Goal title
  doc.setFontSize(19);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.text);
  const titleLines = doc.splitTextToSize(plan.goal_title || 'Financial Plan', 182);
  doc.text(titleLines, 14, y);
  y += titleLines.length * 8.5 + 1;

  // Subtitle meta tags
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...C.muted);
  const metaParts = [];
  if (goalType) metaParts.push(`Goal type: ${goalType}`);
  if (deadline) metaParts.push(`Deadline: ${deadline}`);
  if (metaParts.length) { doc.text(metaParts.join('   ·   '), 14, y); y += 5; }

  doc.setDrawColor(...C.border);
  doc.setLineWidth(0.3);
  doc.line(14, y, 196, y);
  y += 7;

  // ── Goal Overview (4-stat cards) ────────────────────────────────────────
  y = sectionTitle(doc, y, 'Goal Overview');
  const stats = [
    { label: 'Target Amount',     value: fmtAmt(plan.effective_cost, currency) },
    { label: 'Monthly Savings',   value: fmtAmt(plan.monthly_savings_target, currency) },
    { label: 'Duration',          value: `${plan.estimated_months || '?'} months` },
    { label: 'Disposable Income', value: plan.disposable_income ? fmtAmt(plan.disposable_income, currency) : '—' },
  ];
  const cardW = 43, cardH = 16, gap = 2;
  stats.forEach((stat, i) => {
    const cx = 14 + i * (cardW + gap);
    doc.setFillColor(...C.brandLight);
    doc.roundedRect(cx, y, cardW, cardH, 2, 2, 'F');
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...C.brand);
    doc.text(stat.label, cx + cardW / 2, y + 5.5, { align: 'center' });
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...C.text);
    doc.text(stat.value, cx + cardW / 2, y + 12, { align: 'center' });
  });
  y += cardH + 8;

  // ── AI Summary ─────────────────────────────────────────────────────────
  if (plan.summary) {
    y = pageBreakIfNeeded(doc, y, 20);
    y = sectionTitle(doc, y, 'AI Summary');
    doc.setFillColor(250, 250, 253);
    const sumLines = doc.splitTextToSize(plan.summary, 176);
    const sumH = sumLines.length * 4.8 + 7;
    doc.roundedRect(14, y - 2, 182, sumH, 2, 2, 'F');
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(...C.text);
    doc.text(sumLines, 17, y + 3);
    y += sumH + 5;
  }

  // ── AI Analysis (reasoning) ────────────────────────────────────────────
  if (plan.ai_reasoning) {
    y = pageBreakIfNeeded(doc, y, 20);
    y = sectionTitle(doc, y, 'AI Analysis');
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...C.text);
    const paragraphs = plan.ai_reasoning.split('\n').filter((l) => l.trim());
    for (const para of paragraphs) {
      const lines = doc.splitTextToSize(para, 182);
      y = pageBreakIfNeeded(doc, y, lines.length * 4.8 + 4);
      doc.text(lines, 14, y);
      y += lines.length * 4.8 + 3;
    }
    y += 3;
  }

  // ── Projection Chart ────────────────────────────────────────────────────
  if (plan.scenarios?.length > 0) {
    y = pageBreakIfNeeded(doc, y, 88);
    y = sectionTitle(doc, y, 'Savings Projection Chart');
    y = drawProjectionChart(doc, y, plan.scenarios, plan.effective_cost, currency);
  }

  // ── Investment Scenarios table ──────────────────────────────────────────
  if (plan.scenarios?.length > 0) {
    y = pageBreakIfNeeded(doc, y, 30);
    y = sectionTitle(doc, y, 'Investment Scenarios');
    const scenRows = plan.scenarios.map((s) => [
      s.scenario_type ? s.scenario_type.charAt(0).toUpperCase() + s.scenario_type.slice(1) : '—',
      s.instrument_used || s.label || '—',
      s.expected_return_rate != null ? `${(parseFloat(s.expected_return_rate) * 100).toFixed(1)}% p.a.` : '—',
      `${s.estimated_months || plan.estimated_months || '?'} mo`,
      fmtAmt(s.monthly_savings || plan.monthly_savings_target, currency),
      s.description ? s.description.slice(0, 90) + (s.description.length > 90 ? '…' : '') : '—',
    ]);
    autoTable(doc, {
      ...tStyles(),
      startY: y,
      head: [['Scenario', 'Instrument', 'Return', 'Duration', 'Monthly', 'Notes']],
      body: scenRows,
      styles: { ...tStyles().styles, fontSize: 7.5, overflow: 'linebreak' },
      columnStyles: {
        0: { cellWidth: 20, fontStyle: 'bold' },
        1: { cellWidth: 30 },
        2: { cellWidth: 18, halign: 'center' },
        3: { cellWidth: 14, halign: 'center' },
        4: { cellWidth: 25, halign: 'right' },
        5: { cellWidth: 'auto' },
      },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // ── Risk Warnings ──────────────────────────────────────────────────────
  if (plan.risk_warnings?.length > 0) {
    y = pageBreakIfNeeded(doc, y, 20);
    y = sectionTitle(doc, y, 'Risk Warnings');
    const warnRows = plan.risk_warnings.map((w) => [
      '⚠',
      typeof w === 'string' ? w : String(w),
    ]);
    autoTable(doc, {
      startY: y,
      head: [],
      body: warnRows,
      theme: 'plain',
      styles: { fontSize: 8.5, cellPadding: 3.5, textColor: [120, 70, 0], fillColor: C.amberBg, overflow: 'linebreak', lineColor: [251, 191, 36], lineWidth: 0.1 },
      columnStyles: {
        0: { cellWidth: 8, halign: 'center', fontStyle: 'bold', textColor: C.amber },
        1: { cellWidth: 'auto' },
      },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // ── Recommendations ────────────────────────────────────────────────────
  if (plan.recommendations?.length > 0) {
    y = pageBreakIfNeeded(doc, y, 30);
    y = sectionTitle(doc, y, 'AI Recommendations');
    const recRows = plan.recommendations.map((r) => [
      CATEGORY_LABEL[r.category] || (r.category || '—'),
      r.title || '—',
      r.description || '—',
      r.potential_impact ? `+${fmtAmt(r.potential_impact, currency)}/mo` : '—',
      PRIORITY_LABEL[r.priority] ?? `P${r.priority ?? '?'}`,
    ]);
    autoTable(doc, {
      ...tStyles(),
      startY: y,
      head: [['Category', 'Title', 'Description', 'Est. Impact', 'Priority']],
      styles: { ...tStyles().styles, fontSize: 8, overflow: 'linebreak' },
      body: recRows,
      columnStyles: {
        0: { cellWidth: 18, fontStyle: 'bold', textColor: C.brand },
        1: { cellWidth: 38, fontStyle: 'bold' },
        2: { cellWidth: 'auto' },
        3: { cellWidth: 30, halign: 'right', textColor: C.green, fontStyle: 'bold' },
        4: { cellWidth: 18, halign: 'center' },
      },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // ── Month-by-Month Projection table ───────────────────────────────────
  if (plan.monthly_projection?.length > 0) {
    y = pageBreakIfNeeded(doc, y, 30);
    y = sectionTitle(doc, y, 'Month-by-Month Projection');
    const projRows = plan.monthly_projection.map((row) => {
      const rd = plan.created_at ? addMonths(new Date(plan.created_at), row.month - 1) : null;
      return [
        row.month,
        rd ? fmtMonthYear(rd) : '—',
        fmtAmt(row.monthly_addition, currency),
        fmtAmt(row.accumulated_value, currency),
      ];
    });
    autoTable(doc, {
      ...tStyles(),
      startY: y,
      head: [['#', 'Month', 'Monthly Addition', 'Accumulated Value']],
      body: projRows,
      styles: { ...tStyles().styles, fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 10, halign: 'center', textColor: C.muted },
        1: { cellWidth: 30 },
        2: { cellWidth: 50, halign: 'right' },
        3: { cellWidth: 55, halign: 'right', fontStyle: 'bold', textColor: C.brand },
      },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // ── Savings Progress ──────────────────────────────────────────────────
  if (progressData) {
    y = pageBreakIfNeeded(doc, y, 38);
    y = sectionTitle(doc, y, 'Savings Progress');
    const pct = Math.min(100, parseFloat(progressData.percent_complete || 0));
    const remaining = Math.max(0, (plan.effective_cost || 0) - (progressData.total_saved || 0));
    autoTable(doc, {
      ...tStyles(),
      startY: y,
      head: [['Total Saved', 'Completion', 'Entries', 'Remaining']],
      body: [[
        fmtAmt(progressData.total_saved || 0, currency),
        `${pct.toFixed(1)}%`,
        String(progressData.entries?.length || 0),
        fmtAmt(remaining, currency),
      ]],
      didParseCell: (data) => {
        if (data.section === 'body' && data.column.index === 1) {
          data.cell.styles.textColor = pct >= 100 ? C.green : C.brand;
          data.cell.styles.fontStyle  = 'bold';
        }
        if (data.section === 'body' && data.column.index === 3) {
          data.cell.styles.textColor = remaining <= 0 ? C.green : C.text;
        }
      },
    });
    y = doc.lastAutoTable.finalY + 6;

    // Visual progress bar
    if (y < 270) {
      const barX = 14, barW = 182, barH = 5;
      doc.setFillColor(...C.rowAlt);
      doc.roundedRect(barX, y, barW, barH, 2, 2, 'F');
      doc.setFillColor(...C.brand);
      doc.roundedRect(barX, y, Math.max(4, barW * (pct / 100)), barH, 2, 2, 'F');
      doc.setFontSize(7);
      doc.setTextColor(...C.muted);
      doc.text(`${pct.toFixed(1)}% complete`, 105, y + 10, { align: 'center' });
      y += 16;
    }
  }

  // ── Footers on every page ─────────────────────────────────────────────
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawPageFooter(doc, i, totalPages);
  }

  const filename = `plannizzi-${(plan.goal_title || 'plan').replace(/\s+/g, '-').toLowerCase()}.pdf`;
  doc.save(filename);
}

// ─────────────────────────────────────────────────────────────────────────────
// TRANSACTIONS PDF — MONTHLY
// ─────────────────────────────────────────────────────────────────────────────
const MONTH_NAMES_EN = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function drawTxHeader(doc, title, subtitle) {
  doc.setFillColor(...C.brand);
  doc.rect(0, 0, 210, 20, 'F');
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...C.white);
  doc.text(title, 14, 12);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(210, 207, 255);
  doc.text(subtitle, 14, 17);
  doc.text(
    `Generated: ${new Date().toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })}`,
    196, 14, { align: 'right' }
  );
  return 28;
}

export function exportTransactionsMonthPdf(data, year, month, currency = 'BRL', locale = 'en-US') {
  const doc = newDoc();
  const monthName = new Date(year, month - 1, 1).toLocaleDateString(locale, { month: 'long' });
  let y = drawTxHeader(doc, `Spending Report — ${monthName} ${year}`, `Currency: ${currency}`);

  const transactions = data?.transactions || [];
  const summary = data?.summary || { total_expenses: 0, total_income: 0, total_count: 0 };
  const net = parseFloat(summary.total_income) - parseFloat(summary.total_expenses);

  // ── Summary cards ─────────────────────────────────────────────────────────
  y = sectionTitle(doc, y, 'Summary');
  autoTable(doc, {
    ...tStyles(),
    startY: y,
    head: [['Total Expenses', 'Total Income', 'Net Balance']],
    body: [[ fmtAmt(summary.total_expenses, currency), fmtAmt(summary.total_income, currency), fmtAmt(net, currency) ]],
    columnStyles: {
      0: { textColor: C.red,                fontStyle: 'bold' },
      1: { textColor: C.green,              fontStyle: 'bold' },
      2: { textColor: net >= 0 ? C.green : C.red, fontStyle: 'bold' },
    },
  });
  y = doc.lastAutoTable.finalY + 8;

  // ── Transactions ─────────────────────────────────────────────────────────
  y = sectionTitle(doc, y, 'Transactions');
  if (transactions.length === 0) {
    doc.setFontSize(9);
    doc.setTextColor(...C.muted);
    doc.text('No transactions recorded for this month.', 14, y + 4);
  } else {
    const rows = transactions.map((tx) => [
      new Date(tx.tx_date.slice(0, 10) + 'T12:00:00').toLocaleDateString(locale, { day: '2-digit', month: 'short' }),
      tx.description || tx.category,
      tx.category.replace(/_/g, ' '),
      tx.type === 'income' ? 'Income' : 'Expense',
      (tx.type === 'income' ? '+' : '-') + fmtAmt(tx.amount, currency),
    ]);
    autoTable(doc, {
      ...tStyles(),
      startY: y,
      head: [['Date', 'Description', 'Category', 'Type', 'Amount']],
      body: rows,
      styles: { ...tStyles().styles, fontSize: 8, overflow: 'ellipsize' },
      columnStyles: { 4: { halign: 'right', cellWidth: 30 } },
      didParseCell: (data) => {
        if (data.section === 'body' && data.column.index === 4) {
          data.cell.styles.textColor = rows[data.row.index]?.[3] === 'Income' ? C.green : C.red;
        }
      },
    });
  }

  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) { doc.setPage(i); drawPageFooter(doc, i, totalPages); }
  doc.save(`spending-${year}-${String(month).padStart(2, '0')}.pdf`);
}

// ─────────────────────────────────────────────────────────────────────────────
// TRANSACTIONS PDF — YEARLY
// ─────────────────────────────────────────────────────────────────────────────
export function exportTransactionsYearPdf(data, year, currency = 'BRL', locale = 'en-US') {
  const doc = newDoc();
  let y = drawTxHeader(doc, `Annual Spending Report — ${year}`, `Currency: ${currency}`);

  const transactions = data?.transactions || [];
  const monthlySummary = data?.monthly_summary || [];

  y = sectionTitle(doc, y, 'Monthly Summary');

  const totalExp = monthlySummary.reduce((s, r) => s + parseFloat(r.total_expenses), 0);
  const totalInc = monthlySummary.reduce((s, r) => s + parseFloat(r.total_income), 0);

  const summaryRows = monthlySummary.map((r) => {
    const net = parseFloat(r.total_income) - parseFloat(r.total_expenses);
    return [
      new Date(year, r.month - 1, 1).toLocaleDateString(locale, { month: 'long' }),
      fmtAmt(r.total_expenses, currency),
      fmtAmt(r.total_income, currency),
      fmtAmt(net, currency),
    ];
  });
  summaryRows.push(['TOTAL', fmtAmt(totalExp, currency), fmtAmt(totalInc, currency), fmtAmt(totalInc - totalExp, currency)]);

  autoTable(doc, {
    ...tStyles(),
    startY: y,
    head: [['Month', 'Expenses', 'Income', 'Net']],
    body: summaryRows,
    didParseCell: (data) => {
      if (data.section === 'body') {
        const isTotal = data.row.index === summaryRows.length - 1;
        if (isTotal) { data.cell.styles.fontStyle = 'bold'; data.cell.styles.fillColor = C.brandLight; data.cell.styles.textColor = C.brand; }
        if (!isTotal && data.column.index === 1) data.cell.styles.textColor = C.red;
        if (!isTotal && data.column.index === 2) data.cell.styles.textColor = C.green;
        if (!isTotal && data.column.index === 3) {
          const net = parseFloat(r => r);
          data.cell.styles.textColor = summaryRows[data.row.index]?.[3]?.startsWith('-') ? C.red : C.green;
        }
      }
    },
  });
  y = doc.lastAutoTable.finalY + 10;

  if (transactions.length > 0) {
    y = pageBreakIfNeeded(doc, y, 30);
    y = sectionTitle(doc, y, 'All Transactions');
    const txRows = transactions.map((tx) => [
      new Date(tx.tx_date.slice(0, 10) + 'T12:00:00').toLocaleDateString(locale, { day: '2-digit', month: 'short' }),
      tx.description || tx.category,
      tx.category.replace(/_/g, ' '),
      tx.type === 'income' ? 'Income' : 'Expense',
      (tx.type === 'income' ? '+' : '-') + fmtAmt(tx.amount, currency),
    ]);
    autoTable(doc, {
      ...tStyles(),
      startY: y,
      head: [['Date', 'Description', 'Category', 'Type', 'Amount']],
      body: txRows,
      styles: { ...tStyles().styles, fontSize: 7.5, overflow: 'ellipsize' },
      columnStyles: { 4: { halign: 'right', cellWidth: 30 } },
      didParseCell: (data) => {
        if (data.section === 'body' && data.column.index === 4) {
          data.cell.styles.textColor = txRows[data.row.index]?.[3] === 'Income' ? C.green : C.red;
        }
      },
    });
  }

  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) { doc.setPage(i); drawPageFooter(doc, i, totalPages); }
  doc.save(`spending-${year}.pdf`);
}

